/** Shared helpers for Section 9.1 supplemental metrics (detailed report + standalone page). */

import { effectiveUnitsArray } from '../questions/FillInBlanksAnswerField';

export const reportingArticleOrDash = (v) =>
  v != null && String(v).trim() !== '' ? String(v).trim() : '—';

const normalizeValueUnitsForReport = (raw) => {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.filter((u) => typeof u === 'string' && u.trim()).map((u) => u.trim());
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p.filter((u) => typeof u === 'string' && u.trim()).map((u) => u.trim()) : [];
    } catch {
      return [];
    }
  }
  return [];
};

export const formatValueUnitsDisplay = (raw) => {
  const arr = normalizeValueUnitsForReport(raw);
  return arr.length ? arr.join(', ') : '';
};

/** e.g. FY2025 (Jan 1, 2025 – Dec 31, 2025) using question reporting dates */
export const formatFYReportingPeriod = (start, end, locale = 'en') => {
  const loc = locale && String(locale).toLowerCase().startsWith('es') ? 'es' : 'en-US';
  const parseD = (d) => {
    if (d == null || d === '') return null;
    const s = String(d);
    const normalized = s.includes('T') ? s : `${s}T12:00:00`;
    const dt = new Date(normalized);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };
  const s = parseD(start);
  const e = parseD(end);
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  if (s && e) {
    const fyYear = s.getFullYear();
    const left = s.toLocaleDateString(loc, opts);
    const right = e.toLocaleDateString(loc, opts);
    return `FY${fyYear} (${left} – ${right})`;
  }
  const one = s || e;
  if (one) return one.toLocaleDateString(loc, opts);
  return '';
};

export const pdfEscapeText = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Readable line for reporting when stored answer is JSON from fill-in-the-blanks */
export function formatFillInBlanksAnswerDisplay(raw) {
  if (raw == null || String(raw).trim() === '') return '';
  const s = String(raw).trim();
  if (!s.startsWith('{')) return s;
  try {
    const o = JSON.parse(s);
    if (o && Array.isArray(o.blanks)) {
      const blanks = o.blanks.map((b) => String(b ?? '').trim());
      const n = blanks.length;
      const unitArr = effectiveUnitsArray(o, n);
      const anyUnit = unitArr.some((u) => String(u || '').trim() !== '');
      if (anyUnit) {
        return blanks
          .map((bi, i) => {
            const ui = String(unitArr[i] ?? '').trim();
            if (bi && ui) return `${bi} [${ui}]`;
            if (bi) return bi;
            if (ui) return `[${ui}]`;
            return '';
          })
          .filter(Boolean)
          .join(' | ');
      }
      const joined = blanks.filter(Boolean).join(' | ');
      return joined || s;
    }
  } catch {
    /* ignore */
  }
  return s;
}

/** Group Section 9.1 flat rows by sustainability dimension for UI / PDF */
export const groupSection91Rows = (rows) => {
  if (!rows?.length) return [];
  const map = new Map();
  rows.forEach((row) => {
    const key = row.dimensionName || 'General';
    if (!map.has(key)) {
      map.set(key, { dimensionName: key, items: [] });
    }
    map.get(key).items.push(row);
  });
  return Array.from(map.values());
};

/** True when stored answer has real content (including fill-in-blanks JSON with values). */
export function hasSubstantiveSection91Answer(raw) {
  const s = raw == null ? '' : String(raw).trim();
  if (s === '' || s === '—') return false;
  if (s.startsWith('{')) {
    try {
      const o = JSON.parse(s);
      if (o && Array.isArray(o.blanks)) {
        const hasB = o.blanks.some((b) => String(b || '').trim() !== '');
        const n = o.blanks.length;
        const hasU = effectiveUnitsArray(o, n).some((u) => String(u || '').trim() !== '');
        return hasB || hasU;
      }
    } catch {
      return true;
    }
  }
  return true;
}

/** True when the row has a substantive answer (not empty / not em dash placeholder). */
export const section91RowHasAnswer = (row) => hasSubstantiveSection91Answer(row?.answer);

export const buildTopicCompletedRequirements = (report, question, sustainabilityDimension) => {
  const std = String(report.standards || '').toUpperCase();
  const ifrsSasb = std === 'CMF' ? 'CMF NCG 461' : 'IFRS S1/S2';
  const cmfReq =
    question.cmfRequirement && String(question.cmfRequirement).trim() !== ''
      ? question.cmfRequirement
      : [report.disclosureField, report.standards].filter(Boolean).join(' — ') || '—';
  const fieldTitle = report.disclosureField || report.disclosureTopic || '—';
  return {
    ifrsSasb,
    sustainabilityDimension: sustainabilityDimension || '—',
    disclosureTopic: report.disclosureTopic || '—',
    disclosureFieldTitle: fieldTitle,
    sasbAuditCode: question.topicCode || '—',
    cmfRequirements: cmfReq,
    ncg30461Article: reportingArticleOrDash(question.ncg30461Article),
    ncg30519Article: reportingArticleOrDash(question.ncg30519Article),
  };
};

/**
 * Header + Section 9.1 rows from API `reports` payload (same shape as DetailedReport).
 */
export function buildSection91ReportState(apiReports, t, i18n) {
  const companyInfo = apiReports?.[0]?.companyInfo || {};
  const kycData = apiReports?.[0]?.kycData || {};
  const section91Disclosures = [];

  if (apiReports && apiReports.length > 1) {
    const reportsBody = apiReports.slice(1);
    reportsBody.forEach((report) => {
      (report.questions || []).forEach((question) => {
        if (question.section9_1) {
          const answerText = (question.answer != null ? String(question.answer) : '').trim();
          const hasAnswer = hasSubstantiveSection91Answer(answerText);
          const reasonTrim =
            question.noAnswerReason != null && String(question.noAnswerReason).trim() !== ''
              ? String(question.noAnswerReason).trim()
              : '';
          const units = formatValueUnitsDisplay(question.valueUnitFormat);
          const periodLabel = formatFYReportingPeriod(
            question.reportingPeriodStart,
            question.reportingPeriodEnd,
            i18n.language
          );
          const rawDimension = question.dimension || question.sector || 'General';
          const dimensionKey = rawDimension === 'Environmental' ? 'Environment' : rawDimension;
          section91Disclosures.push({
            rowKey: `${report.assignmentId ?? 'na'}_${question.id}`,
            dimensionName: dimensionKey,
            disclosureTopic: report.disclosureTopic,
            disclosureField: report.disclosureField,
            standards: report.standards,
            engagementQuestion: question.engagementQuestion || '—',
            completedRequirements: buildTopicCompletedRequirements(report, question, dimensionKey),
            questionIdentifier: question.questionIdentifier || '—',
            sasbMetricName: question.sasbMetricName || '—',
            answer: answerText || '—',
            valueUnits: units || '—',
            reportingPeriod: periodLabel || '—',
            noAnswerReason: !hasAnswer && reasonTrim ? reasonTrim : '—',
          });
        }
      });
    });
  }

  return {
    clientName: companyInfo.companyName || t('reporting.companyName'),
    sasbSector: kycData.sasbSector || t('reporting.generalSector'),
    sasbIndustry: kycData.sasbIndustry || t('reporting.generalIndustry'),
    reportingPeriod: `${t('reporting.fiscalYear')} ${new Date().getFullYear()}`,
    reportDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    section91Disclosures,
  };
}

/**
 * Full HTML document for print-to-PDF (header + Section 9.1 only).
 */
export function buildSection91PrintDocumentHtml(reportData, t) {
  const section91Html =
    (reportData.section91Disclosures || []).length > 0
      ? `<div class="section">
            <h2 style="margin-top: 20px;">${pdfEscapeText(t('reporting.section91Title'))}</h2>
            ${groupSection91Rows(reportData.section91Disclosures || [])
              .map(
                (dim, dimIdx) => `
            <h3 style="margin-top: 22px; font-size: 15px; color: #0f766e; border-bottom: 2px solid #99f6e4; padding-bottom: 6px;">9.1.${dimIdx + 1} ${pdfEscapeText(t('reporting.sustainabilityDimension'))}: ${pdfEscapeText(dim.dimensionName)}</h3>
            <p style="font-size: 12px; color: #4b5563; margin: 8px 0 14px 0;"><em>${pdfEscapeText(t('reporting.section91DimensionDefinition', { dimension: dim.dimensionName }))}</em></p>
            ${dim.items
              .map((row) => {
                const req = row.completedRequirements;
                const reqHtml = req
                  ? `
                <div style="margin-bottom: 14px;">
                  <p><strong>${pdfEscapeText(t('reporting.completedRequirements'))}:</strong></p>
                  <div class="requirements-grid">
                    <div class="requirement-box ifrs-box">
                      <strong>${pdfEscapeText(t('reporting.sustainabilityDimension'))}:</strong><br>
                      <span style="font-size:12px;color:#1d4ed8;font-weight:500;">${pdfEscapeText(dim.dimensionName)}</span>
                    </div>
                    <div class="requirement-box disclosure-topic-box">
                      <strong>${pdfEscapeText(t('reporting.disclosureTopicLabel'))}:</strong><br>
                      <span style="font-size:11px;font-weight:600;color:#155e75;white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;line-height:1.35;display:block;">${pdfEscapeText(req.disclosureFieldTitle || '—')}</span>
                    </div>
                    <div class="requirement-box audit-box">
                      <strong>${pdfEscapeText(t('reporting.sasbAuditCodeLabel'))}:</strong><br>${pdfEscapeText(req.sasbAuditCode)}
                    </div>
                    <div class="requirement-box" style="background:#eef2ff;border-color:#c7d2fe;">
                      <strong>${pdfEscapeText(t('reporting.section91ColReportingPeriod'))}:</strong><br>
                      ${pdfEscapeText(row.reportingPeriod)}
                    </div>
                    <div class="requirement-box article-box">
                      <strong>${pdfEscapeText(t('reporting.cmfNcg30461Article'))}:</strong><br>
                      <span style="white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;line-height:1.35;display:block;">${pdfEscapeText(req.ncg30461Article)}</span>
                    </div>
                    <div class="requirement-box article-box">
                      <strong>${pdfEscapeText(t('reporting.cmfNcg30519Article'))}:</strong><br>
                      <span style="white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;line-height:1.35;display:block;">${pdfEscapeText(req.ncg30519Article)}</span>
                    </div>
                  </div>
                  <div style="margin-top:10px;display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.15fr) minmax(0,0.55fr);gap:10px;align-items:stretch;">
                    <div class="requirement-box" style="background:#f0fdfa;border-color:#99f6e4;min-width:0;display:flex;flex-direction:column;">
                      <strong>${pdfEscapeText(t('reporting.section91ColSasbMetric'))}:</strong><br>
                      <span style="font-size:11px;flex:1;">${pdfEscapeText(row.sasbMetricName)}</span>
                    </div>
                    <div style="padding:10px 12px;background:#f0fdf4;border:1px solid #86efac;border-radius:6px;min-width:0;display:flex;flex-direction:column;">
                      <strong style="color:#166534;">${pdfEscapeText(t('reporting.cmfRequirements'))}:</strong>
                      <p style="margin:6px 0 0 0;font-size:11px;line-height:1.45;color:#14532d;white-space:pre-wrap;flex:1;">${pdfEscapeText(req.cmfRequirements)}</p>
                    </div>
                    <div class="requirement-box" style="background:#f0f9ff;border-color:#bae6fd;min-width:0;display:flex;flex-direction:column;">
                      <strong>${pdfEscapeText(t('reporting.section91ColValueUnits'))}:</strong><br>
                      <span style="flex:1;">${pdfEscapeText(row.valueUnits)}</span>
                    </div>
                  </div>
                </div>`
                  : '';
                const hasPdfAns = section91RowHasAnswer(row);
                const answerBlock = hasPdfAns
                  ? `<div style="margin: 12px 0; padding: 12px; background: #ecfdf5; border-left: 4px solid #16a34a; border-radius: 0 8px 8px 0;">
                    <p style="margin: 0 0 6px 0; font-size: 12px;"><strong>${pdfEscapeText(t('reporting.answer'))}:</strong></p>
                    <p style="margin: 0; font-size: 12px; white-space: pre-wrap;">${pdfEscapeText(row.answer)}</p>
                  </div>`
                  : `<div style="margin: 12px 0; padding: 12px; background: #fffbeb; border-left: 4px solid #d97706; border-radius: 0 8px 8px 0;">
                    <p style="margin: 0 0 6px 0; font-size: 12px;"><strong>${pdfEscapeText(t('reporting.section91ColNoAnswerReason'))}:</strong></p>
                    <p style="margin: 0; font-size: 12px; white-space: pre-wrap;">${pdfEscapeText(row.noAnswerReason)}</p>
                  </div>`;
                return `
            <div style="margin: 16px 0; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; background: #fafafa;">
              ${reqHtml}
              <div style="padding: 12px 14px; background: #fff; border-bottom: 1px solid #f3f4f6;">
                <p style="margin: 0; font-size: 12px; line-height: 1.5; color: #111827;"><strong>${pdfEscapeText(t('reporting.question'))}:</strong> ${pdfEscapeText(row.engagementQuestion)}</p>
              </div>
              ${answerBlock}
            </div>`;
              })
              .join('')}
                `
              )
              .join('')}
          </div>`
      : `<div class="section"><p style="color:#6b7280;font-style:italic;">${pdfEscapeText(t('reporting.section91ReportEmpty'))}</p></div>`;

  return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${pdfEscapeText(reportData.clientName)} — ${pdfEscapeText(t('reporting.section91Title'))}</title>
          <style>
            @page { margin: 0; size: auto; padding-block: 1in; padding-inline: 1in; }
            body { font-family: Arial, sans-serif; margin: 0; padding: 20px; padding-top: 40px; color: #333; line-height: 1.4; }
            .header {
              background: linear-gradient(to right, #059669, #0d9488);
              color: white; padding: 30px; text-align: center; margin-bottom: 30px; border-radius: 8px;
            }
            h1 { font-size: 28px; margin-bottom: 20px; text-decoration: underline; text-decoration-color: rgba(255,255,255,0.3); }
            h2 { font-size: 20px; margin: 25px 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #059669; color: #059669; }
            .requirements-grid {
              display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; margin: 15px 0;
            }
            .requirement-box { padding: 10px; border-radius: 6px; font-size: 11px; }
            .ifrs-box { background-color: #dbeafe; border: 1px solid #93c5fd; }
            .disclosure-topic-box { background-color: #ecfeff; border: 1px solid #22d3ee; }
            .audit-box { background-color: #fffbeb; border: 1px solid #fbbf24; }
            .article-box { background-color: #f5f3ff; border: 1px solid #a78bfa; }
            .section { margin-top: 20px; margin-bottom: 30px; page-break-inside: avoid; }
            @media print { body { margin: 0; padding: 10px; } .header { margin: 0 0 20px 0; } .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${pdfEscapeText(t('reporting.sustainabilityDisclosureReport'))}</h1>
            <h2>${pdfEscapeText(reportData.clientName)}</h2>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; max-width: 600px; margin: 20px auto; text-align: left; font-size: 14px;">
              <div><strong>${pdfEscapeText(t('reporting.sasbSector'))}:</strong> ${pdfEscapeText(reportData.sasbSector)}</div>
              <div><strong>${pdfEscapeText(t('reporting.sasbIndustry'))}:</strong> ${pdfEscapeText(reportData.sasbIndustry)}</div>
              <div><strong>${pdfEscapeText(t('reporting.reportingPeriod'))}:</strong> ${pdfEscapeText(reportData.reportingPeriod)}</div>
              <div><strong>${pdfEscapeText(t('reporting.reportDate'))}:</strong> ${pdfEscapeText(reportData.reportDate)}</div>
            </div>
          </div>
          ${section91Html}
          <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ccc; text-align: center; font-size: 11px; color: #666;">
            <p>${pdfEscapeText(t('reporting.generatedByEcomina'))} ${new Date().toLocaleDateString()}</p>
            <p>${pdfEscapeText(t('reporting.confidentialInternalUse'))}</p>
          </div>
        </body>
        </html>
      `;
}
